/**
 * Presentational config + the row shape rendered by the Categories table.
 *
 * The table is wired to `useCategoryTreeQuery` and renders the nested category
 * hierarchy via the DataGrid's expandable rows (`getChildren` → `children`).
 *
 * `Category` is the row shape: a tree node (id/name/slug/imageUrl/parentId +
 * `children`) plus the per-category aggregates. The `/tree` endpoint does not
 * return aggregates today, so the hook maps them to `0`; this row type already
 * matches what the planned `categories/treeDetails` endpoint will return, so
 * the swap-over is just changing the query + dropping the zero-fill.
 */
export type Category = {
    id: string;
    name: string;
    slug: string;
    imageUrl: string | null;
    parentId: string | null;
    productCount: number;
    /**
     * Rolled up in integer tetri (BOS-162). A category tree sums its children,
     * and summing decimals down a deep tree is where a rollup and the rows
     * under it stop agreeing.
     */
    totalCostTetri: number;
    totalSalePriceTetri: number;
    /** Markup over cost, in percent — not money. */
    totalMarkup: number;
    children: Category[];
};

/**
 * A selectable parent in the Add/Edit form's parent picker. `depth` drives the
 * indentation that mirrors the tree nesting in the flat dropdown list.
 */
export type CategoryParentOption = {
    id: string;
    name: string;
    depth: number;
};

export type CategoryRowAction = 'manageAttributes' | 'edit' | 'delete';

/** Icon shown in the thumbnail cell when a category has no image. */
export const NO_IMAGE_ICON = 'carbon:no-image';

/** Stable column ids — shared between the View columns and sort descriptor. */
export const CATEGORY_COLUMN = {
    image: 'image',
    name: 'name',
    slug: 'slug',
    products: 'products',
    totalCost: 'totalCost',
    totalSalePrice: 'totalSalePrice',
    totalMarkup: 'totalMarkup',
    actions: 'actions',
} as const;

export const CATEGORY_ROW_ACTIONS: {
    id: CategoryRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
}[] = [
    {
        id: 'manageAttributes',
        icon: 'gravity-ui:tag',
        labelKey:
            'categories.components.categoryTable.actions.manageAttributes',
    },
    {
        id: 'edit',
        icon: 'gravity-ui:pencil',
        labelKey: 'categories.components.categoryTable.actions.edit',
    },
    {
        id: 'delete',
        icon: 'gravity-ui:trash-bin',
        labelKey: 'categories.components.categoryTable.actions.delete',
        variant: 'danger',
    },
];

/**
 * Default number of **root** categories shown per page (the tree paginates its
 * top level client-side; each root keeps its whole subtree).
 */
/**
 * Markup for display: `108.3 %`.
 *
 * It is a percentage of cost, and it sits between two currency columns, so
 * rendering it as a bare "108.30" read as 108 GEL (BOS-137). One decimal —
 * the second was never meaningful on a ratio of two rounded sums.
 */
export const formatMarkupPercent = (value: number): string =>
    `${MARKUP_FORMATTER.format(value)} %`;

const MARKUP_FORMATTER = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
});

export const CATEGORIES_PAGE_SIZE = 10;

/** Selectable page sizes for the categories table's rows-per-page control. */
export const CATEGORIES_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};
